import PlusIcon from "@rsuite/icons/Plus";
import { useContext, useEffect, useState, type FC } from "react";
import { Checkbox, IconButton, Pagination, Table } from "rsuite";
import MainLayout from "../components/MainLayout";
import { LoadingContext } from "../contexts/LoadingContext";
import { SearchContext } from "../contexts/SearchContext";
import { MasterProduct } from "../models/inventory/master_product";
import { PaginationResponse } from "../objects/pagination";
import { BsCart3, BsEye, BsTrash3 } from "react-icons/bs";
import { errorToast, successToast } from "../utils/helper-ui";
import {
  convertToProducts,
  createMasterProduct,
  deleteMasterProduct,
  getMasterProducts,
  updateMasterProduct,
} from "../services/inventory/MasterProductApi";
import { getPagination, money } from "../utils/helper";
import MasterProductForm from "../components/features/inventory/MasterProductForm";
import { useNavigate } from "react-router-dom";
import { ValueType } from "rsuite/esm/Checkbox";
import TagIcon from "@rsuite/icons/Tag";
const { Column, HeaderCell, Cell } = Table;
interface MasterProductPageProps {}

const MasterProductPage: FC<MasterProductPageProps> = ({}) => {
  const { search, setSearch } = useContext(SearchContext);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(30);
  const [pagination, setPagination] = useState<PaginationResponse>();
  const [masterProducts, setMasterProducts] = useState<MasterProduct[]>([]);
  const [modalFormOpen, setModalFormOpen] = useState(false);
  const nav = useNavigate();
  const [selectedMasterProduct, setSelectedMasterProduct] =
    useState<MasterProduct>();

  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys.length === masterProducts.length) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (
    checkedKeys.length > 0 &&
    checkedKeys.length < masterProducts.length
  ) {
    indeterminate = true;
  }

  const handleCheckAll = (
    value: ValueType | undefined,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked ? masterProducts.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };
  const handleCheck = (
    value: ValueType | undefined,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked
      ? [...checkedKeys, value as string]
      : checkedKeys.filter((item) => item !== (value as string));
    setCheckedKeys(keys);
  };

  useEffect(() => {
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    fetchData();
  }, [mounted, search, page, size]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getMasterProducts({ page, size, search });
      const resJson = await res.json();
      //   console.log(data);
      setMasterProducts(resJson.data.items);
      setPagination(getPagination(resJson.data));
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold">Master Product</h1>
      {/* button */}
      <div className="flex flex-row gap-2">
        {checkedKeys.length > 0 && (
          <IconButton
            size="sm"
            appearance="primary"
            color="blue"
            icon={<TagIcon className="" />}
            onClick={async () => {
              if (
                window.confirm(
                  `Are you sure you want to convert (${
                    checkedKeys.length
                  }) master product : ${checkedKeys
                    .map(
                      (id) =>
                        `\n- ${
                          masterProducts.find((item) => item.id === id)?.name
                        }`
                    )
                    .join("")}\n to product?`
                )
              ) {
                try {
                  setIsLoading(true);
                  let resp = await convertToProducts(checkedKeys);
                  let respJson = await resp.json();
                  successToast(respJson.message);
                  if (respJson.errors.length > 0) {
                    errorToast(`${respJson.errors.map((item: any) => `- ${item}\n`).join("")}`);
                  }
                  setCheckedKeys([]);
                  fetchData();
                } catch (error) {
                  errorToast(`${error}`);
                } finally {
                  setIsLoading(false);
                }
              }
            }}
          >
            Convert To Product
          </IconButton>
        )}

        <IconButton
          size="sm"
          appearance="primary"
          color="green"
          icon={<PlusIcon />}
          onClick={() => setModalFormOpen(true)}
        >
          Create Master Product
        </IconButton>
      </div>
    </div>
  );

  const removeMasterProduct = async (masterProduct: MasterProduct) => {
    try {
      setIsLoading(true);
      if (
        window.confirm(
          `Are you sure you want to delete masterProduct with name ${masterProduct.name}?`
        )
      ) {
        await deleteMasterProduct(masterProduct.id!);
        fetchData();
      }
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveMasterProduct = async (masterProduct: MasterProduct) => {
    try {
      setIsLoading(true);
      if (selectedMasterProduct) {
        await updateMasterProduct(selectedMasterProduct.id!, masterProduct);
      } else {
        await createMasterProduct(masterProduct);
      }
      setModalFormOpen(false);
      setSelectedMasterProduct(undefined);
      fetchData();
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainLayout header={renderHeader()}>
      <Table minHeight={700} data={masterProducts} onRowClick={(data, i) => {}}>
        <Column width={50} align="center" fixed>
          <HeaderCell style={{ padding: 0 }}>
            <div style={{ lineHeight: "40px" }}>
              <Checkbox
                inline
                checked={checked}
                indeterminate={indeterminate}
                onChange={handleCheckAll}
              />
            </div>
          </HeaderCell>
          <Cell style={{ padding: 0 }}>
            {(rowData) => (
              <div style={{ lineHeight: "46px" }}>
                <Checkbox
                  value={rowData.id}
                  inline
                  onChange={handleCheck}
                  checked={checkedKeys.some((item) => item === rowData.id)}
                />
              </div>
            )}
          </Cell>
        </Column>
        <Column width={70} align="left" fixed>
          <HeaderCell>No</HeaderCell>
          <Cell>
            {(rowData) => (
              <div>
                {(page - 1) * size +
                  masterProducts.indexOf(rowData as MasterProduct) +
                  1}
              </div>
            )}
          </Cell>
        </Column>

        <Column width={200} resizable fixed>
          <HeaderCell>Name</HeaderCell>
          <Cell>
            {(rowData) => (
              <div
                className="hover:underline cursor-pointer font-semibold "
                onClick={() => {
                 nav(`/master-product/${rowData.id}`)
                }}
              >
                {rowData.name}
              </div>
            )}
          </Cell>
        </Column>

        <Column width={300} resizable>
          <HeaderCell>Description</HeaderCell>
          <Cell dataKey="description" />
        </Column>

        <Column width={150} resizable>
          <HeaderCell>SKU</HeaderCell>
          <Cell dataKey="sku" />
        </Column>

        <Column width={150} resizable>
          <HeaderCell>Barcode</HeaderCell>
          <Cell dataKey="barcode" />
        </Column>

        <Column width={100} resizable>
          <HeaderCell>Price</HeaderCell>
          <Cell>{(rowData) => <div>{money(rowData.price)}</div>}</Cell>
        </Column>
        <Column width={150} resizable>
          <HeaderCell>Brand</HeaderCell>
          <Cell>
            {(rowData) => <div>{rowData.brand ? rowData.brand.name : ""}</div>}
          </Cell>
        </Column>
        <Column width={150} resizable>
          <HeaderCell>Category</HeaderCell>
          <Cell>
            {(rowData) => (
              <div>{rowData.category ? rowData.category.name : ""}</div>
            )}
          </Cell>
        </Column>
        <Column width={200} resizable>
          <HeaderCell>Action</HeaderCell>
          <Cell>
            {(rowData) => (
              <div className="flex flex-row gap-2">
                <BsTrash3
                  className="text-red-500 hover:text-red-800 cursor-pointer"
                  size={16}
                  onClick={() => removeMasterProduct(rowData as MasterProduct)}
                />
                <BsEye
                  className="text-green-500 hover:text-green-800 cursor-pointer"
                  size={16}
                  onClick={() => nav(`/master-product/${rowData.id}`)}
                />
              </div>
            )}
          </Cell>
        </Column>
      </Table>
      <div style={{ padding: 20 }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={["total", "-", "limit", "|", "pager", "skip"]}
          total={pagination?.total ?? 0}
          limitOptions={[10, 30, 50]}
          limit={pagination?.size}
          activePage={pagination?.page}
          onChangePage={setPage}
          onChangeLimit={setSize}
        />
      </div>
      <MasterProductForm
        masterProduct={selectedMasterProduct}
        open={modalFormOpen}
        onClose={() => {
          setModalFormOpen(false);
          setSelectedMasterProduct(undefined);
        }}
        onSubmit={saveMasterProduct}
      />
    </MainLayout>
  );
};
export default MasterProductPage;
